import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, A11y } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import style from './Features.module.css'

import screenshotShortlist from '../../../assets/images/screenshot-shortlist.png'
import screenshotCV from '../../../assets/images/screenshot-cv.png'
import screenshotJobFit from '../../../assets/images/screenshot-jobfit.png'
import screenshotFeed from '../../../assets/images/screenshot-feed.png'
import screenshotGraph from '../../../assets/images/screenshot-graph.png'

const features = [
  {
    id: 'shortlist',
    eyebrow: 'Weekly Shortlist',
    title: 'Your 20 things worth doing this week',
    description:
      'Every week, Chimp reads the market and generates a ranked shortlist of jobs, courses, events, and news — each matched to your CV with a clear "why this, why now" and a next action to take. No feed. No scrolling. Just your shortlist.',
    img: screenshotShortlist,
  },
  {
    id: 'cv',
    eyebrow: 'CV Analyzer',
    title: 'An honest read on your CV',
    description:
      "Get a detailed breakdown of your CV's strengths, gaps, and overall score — plus the roles you're competitive for today and the titles to aim for next. Built on what 45,000 companies in Sweden are actually hiring for.",
    img: screenshotCV,
  },
  {
    id: 'jobfit',
    eyebrow: 'Job Fit',
    title: 'Know exactly where you stand before you apply',
    description:
      "Drop any job into Chimp and get an instant fit score. See which requirements you match, which you're missing, and what to do about it — before you spend an hour on an application that won't land.",
    img: screenshotJobFit,
  },
  {
    id: 'feed',
    eyebrow: 'Your Briefing',
    title: 'Browse, spot, act — without the noise',
    description:
      'A focused stream of jobs, courses, events and news filtered to what matters for you. See something interesting? Drop it straight into the AI chat — Chimp will score your fit, surface the gaps, and tell you what to do next.',
    img: screenshotFeed,
  },
  {
    id: 'graph',
    eyebrow: 'Discovery Universe',
    title: 'See your entire career landscape at once',
    description:
      'Opportunities mapped as a universe around you — clustered by industry, technology, and type. See where your career sits today, spot patterns across hundreds of roles, and find paths you would never have scrolled to.',
    img: screenshotGraph,
  },
]

/* ─── Browser frame shared between desktop + mobile ─── */
const BrowserFrame = ({ img, label }) => (
  <div className={style.browser}>
    <div className={style.browserBar}>
      <div className={style.browserDots}>
        <span className={style.red} />
        <span className={style.yellow} />
        <span className={style.green} />
      </div>
      <div className={style.browserUrl}>chimpknows.com</div>
    </div>
    <div className={style.screenshotWrap}>
      <img src={img} alt={label} className={style.screenshot} />
    </div>
  </div>
)

const Features = () => {
  const [activeIndex, setActiveIndex] = useState(0)
  const itemRefs = useRef([])

  useEffect(() => {
    const observers = itemRefs.current.map((el, i) => {
      if (!el) return null
      const observer = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveIndex(i) },
        { rootMargin: '-35% 0px -55% 0px', threshold: 0 }
      )
      observer.observe(el)
      return observer
    })
    return () => observers.forEach(obs => obs?.disconnect())
  }, [])

  return (
    <section className={style.section} id="features">
      <motion.div
        className={style.header}
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.55 }}
      >
        <span className={style.eyebrow}>Features</span>
        <h2 className={style.title}>Everything you need. Nothing you don't.</h2>
        <p className={style.subtitle}>
          Built on factual data from 45,000 companies in Sweden — not posts, not influencers.
        </p>
      </motion.div>

      {/* ── Desktop: sticky scroll layout ── */}
      <div className={style.showcase}>
        <div className={style.featureList}>
          {features.map(({ id, eyebrow, title, description }, i) => (
            <div
              key={id}
              className={`${style.featureItem} ${i === activeIndex ? style.active : ''}`}
              ref={el => { itemRefs.current[i] = el }}
            >
              <span className={style.featureEyebrow}>{eyebrow}</span>
              <h3 className={style.featureTitle}>{title}</h3>
              <p className={style.featureDesc}>{description}</p>
            </div>
          ))}
        </div>

        <div className={style.stickyPanel}>
          <div className={style.dots}>
            {features.map((_, i) => (
              <button
                key={i}
                className={`${style.dot} ${i === activeIndex ? style.dotActive : ''}`}
                onClick={() => itemRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                aria-label={`Go to feature ${i + 1}`}
              />
            ))}
          </div>
          <div className={style.browserWrap}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.28, ease: 'easeOut' }}
              >
                <BrowserFrame img={features[activeIndex].img} label={features[activeIndex].eyebrow} />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* ── Mobile: Swiper carousel ── */}
      <div className={style.mobileShowcase}>
        <Swiper
          modules={[Pagination, A11y]}
          spaceBetween={24}
          slidesPerView={1.08}
          centeredSlides
          pagination={{ clickable: true }}
          className={style.swiper}
        >
          {features.map(({ id, eyebrow, title, description, img }) => (
            <SwiperSlide key={id} className={style.slide}>
              <BrowserFrame img={img} label={eyebrow} />
              <div className={style.slideText}>
                <span className={style.featureEyebrow}>{eyebrow}</span>
                <h3 className={style.slideTitle}>{title}</h3>
                <p className={style.slideDesc}>{description}</p>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  )
}

export default Features
